import type {
  CandidateWebCallPayload,
  CheckWebLinkResponse,
  LanguageRegistryResponse,
  PermanentLinkQuestionsResponse,
  ScheduledAIWebCallDetailsResponse,
  UninterestedInfoResponse,
  WebCallSettingsResponse,
  WithdrawalInfoResponse,
} from 'api/generated/types';

/**
 * A request the interview app starts at boot, before its page has loaded, and
 * hands to the page code that would otherwise send it (see
 * startInterviewBootRequests.ts). Each slot holds at most one request and is
 * read once: the first `take` closes it, so the page never reuses a response
 * from an earlier load and the boot code never sends a request the page has
 * already sent itself.
 */
export interface BootRequest<T> {
  /**
   * Sends the request for `key` with `send` unless the page has already asked
   * for this slot.
   */
  start: (key: string, send: () => Promise<T>) => void;
  /**
   * The request started for `key`, or null when none was (or one for another
   * key was). Closes the slot either way.
   */
  take: (key: string) => Promise<T> | null;
}

export function createBootRequest<T>(): BootRequest<T> {
  let closed = false;
  let started: { key: string; response: Promise<T> } | null = null;
  return {
    start: (key, send) => {
      if (closed || started) return;
      const response = send();
      // The page awaits this and handles a failure; until it takes it, the
      // rejection must not surface as unhandled.
      response.catch(() => {
        // swallow-ok: the page that takes this request handles its failure
      });
      started = { key, response };
    },
    take: (key) => {
      closed = true;
      const response = started?.key === key ? started.response : null;
      started = null;
      return response;
    },
  };
}

/**
 * The invite link's check_web_link, for the page it opens (useLinkResolution
 * on the landing page, useSchedulingCampaignDisplay on the scheduling page).
 */
export const bootLinkCheck = createBootRequest<CheckWebLinkResponse>();
export const linkCheckRequestKey = (payload: CandidateWebCallPayload): string =>
  JSON.stringify([payload.webCallEncodedId ?? null, payload.callId ?? null]);

/**
 * The scheduling page's check_web_link payload (useSchedulingCampaignDisplay):
 * the session's call when it has one, else the link.
 */
export const schedulingLinkCheckPayload = (
  sessionCallId: string | null | undefined,
  linkId: string | null | undefined
): CandidateWebCallPayload => ({
  webCallEncodedId: sessionCallId ? undefined : linkId || undefined,
  callId: sessionCallId || undefined,
});

/**
 * The landing page's link check, handed to the scheduling page it redirects
 * to (useSchedulingCampaignDisplay), keyed by the scheduling page's payload.
 */
export const schedulingLinkHandoff = createBootRequest<CheckWebLinkResponse>();

/** The job's candidate-facing settings (useWebCallCampaignSettings). */
export const bootWebCallSettings = createBootRequest<WebCallSettingsResponse>();

/** The job's intake form questions (CandidateForm). */
export const bootLinkQuestions =
  createBootRequest<PermanentLinkQuestionsResponse>();

/** The interview-language registry (useLanguageRegistry), which has no inputs. */
export const bootLanguageRegistry =
  createBootRequest<LanguageRegistryResponse>();
export const LANGUAGE_REGISTRY_REQUEST_KEY = 'languages';

/** The scheduled interview a reschedule link opens (RescheduleReasonPage), keyed by its id. */
export const bootScheduledInterview =
  createBootRequest<ScheduledAIWebCallDetailsResponse>();

/** The not-interested page's info (UninterestedPage), keyed by the link's token. */
export const bootUninterestedInfo =
  createBootRequest<UninterestedInfoResponse>();

/** The withdrawal page's info (WithdrawalPage), keyed by the link's token. */
export const bootWithdrawalInfo = createBootRequest<WithdrawalInfoResponse>();
